import type { RowData, Column } from "@tanstack/react-table";
import type { CSSProperties } from "react";
import type { DataTableRowClickHandler } from "./DataTable";
import { cn } from "../../utils";

export function getColumnPinningInfo(column: Column<any, any>) {
  const isPinned = column.getIsPinned();

  const isLastLeftPinnedColumn = isPinned === "left" && column.getIsLastColumn("left");
  const isFirstRightPinnedColumn = isPinned === "right" && column.getIsFirstColumn("right");

  return { isPinned, isLastLeftPinnedColumn, isFirstRightPinnedColumn };
}

export function getColumnPinningStyles(column: Column<any, any>): {
  classes: string;
  style: Pick<CSSProperties, "left" | "right">;
} {
  const { isPinned, isFirstRightPinnedColumn, isLastLeftPinnedColumn } = getColumnPinningInfo(column);

  const classes = cn(
    isPinned ? "sticky" : "relative",
    isPinned ? "z-[1]" : "z-0",
    isLastLeftPinnedColumn && "shadow-[inset_-1px_0_0_0_black]",
    isFirstRightPinnedColumn && "shadow-[inset_1px_0_0_0_black]"
  );

  const style: CSSProperties = {
    left: isPinned === "left" ? column.getStart("left") : undefined,
    right: isPinned === "right" ? column.getAfter("right") : undefined
  };

  return { classes, style };
}

type RowClickHandlers = {
  onClick: (event: React.MouseEvent<HTMLTableRowElement>) => void;
};

type RowClickContext<TData extends RowData> = {
  rowData: Parameters<DataTableRowClickHandler<TData>>[0];
  row: Parameters<DataTableRowClickHandler<TData>>[1]["row"];
  table: Parameters<DataTableRowClickHandler<TData>>[1]["table"];
};

export function getRowClickHandlers<TData extends RowData>(
  handler: DataTableRowClickHandler<TData>,
  { rowData, row, table }: RowClickContext<TData>
): RowClickHandlers {
  const handleClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
    if (event.detail === 1) {
      handler(rowData, { event, row, table, clickType: "single" });
    }
    if (event.detail >= 2) {
      handler(rowData, { event, row, table, clickType: "double" });
    }
  };
  return {
    onClick: handleClick
  };
}
